/**
 * Thin, dependency-free wrapper around the browser Geolocation API.
 *
 * All helpers here are defensive: geolocation may be unavailable (older
 * browsers, insecure origins) or denied by the user. In those cases we resolve
 * to `undefined`/reject gracefully so callers can stay backward compatible and
 * never block the user.
 */

export type Coordinates = {
  latitude: number;
  longitude: number;
};

/**
 * Formats a coordinate pair into the "lat, lng" string stored in the
 * GPS Coordinates field, e.g. "59.329323, 18.068581".
 */
export function formatCoordinates({ latitude, longitude }: Coordinates): string {
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
}

export function isGeolocationSupported(): boolean {
  return (
    typeof navigator !== 'undefined' && 'geolocation' in navigator
  );
}

/**
 * Requests the current device position. Requesting triggers the browser's
 * native permission prompt the first time it is called.
 *
 * Resolves with a formatted "lat, lng" string, or rejects if geolocation is
 * unsupported, denied or times out.
 */
export function getCurrentCoordinates(): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!isGeolocationSupported()) {
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve(
          formatCoordinates({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }),
        );
      },
      (error) => reject(error),
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      },
    );
  });
}

